import React from "react";
import { Phone, Mail, MapPin, Facebook, Instagram } from "lucide-react";
import { BUSINESS } from "@/lib/api";
import { useT } from "@/lib/i18n";

const SOCIAL_LINKS = [
  { href: BUSINESS.socials?.facebook, label: "Follow Oxford Cleaners on Facebook", Icon: Facebook, key: "facebook" },
  { href: BUSINESS.socials?.instagram, label: "Follow Oxford Cleaners on Instagram", Icon: Instagram, key: "instagram" },
].filter((s) => !!s.href);

export default function Footer() {
  const { t } = useT();
  return (
    <footer
      data-testid="site-footer"
      className="bg-brand-navy text-white pt-16 pb-10"
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8 grid md:grid-cols-12 gap-10">
        <div className="md:col-span-5">
          <div className="flex items-center gap-2.5">
            <span className="h-9 w-9 rounded-full bg-brand-blue grid place-items-center font-display font-bold text-lg">
              O
            </span>
            <span className="font-display text-xl font-bold tracking-tight">
              Oxford Cleaners
            </span>
          </div>
          <p className="mt-4 text-slate-300 leading-relaxed max-w-sm">
            {t("footer.about")}
          </p>
          <div className="mt-5 flex items-center gap-2">
            <div className="us-stripe h-1 w-16 rounded-full" />
            <span className="text-xs uppercase tracking-[0.2em] text-slate-300 font-semibold">
              {t("footer.proudly")}
            </span>
          </div>
          {SOCIAL_LINKS.length > 0 && (
            <div className="mt-6 flex items-center gap-3" data-testid="footer-socials">
              {SOCIAL_LINKS.map(({ href, label, Icon, key }) => (
                <a
                  key={key}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={label}
                  data-testid={`footer-social-${key}`}
                  className="h-10 w-10 rounded-full bg-white/10 hover:bg-brand-blue text-white grid place-items-center transition-colors"
                >
                  <Icon className="h-4 w-4" />
                </a>
              ))}
            </div>
          )}
        </div>

        <div className="md:col-span-3">
          <p className="font-display font-semibold uppercase text-sm tracking-[0.18em] text-slate-300">
            {t("footer.servicesHeading")}
          </p>
          <ul className="mt-4 space-y-2 text-slate-300">
            <li><a href="#services" className="hover:text-white">Dry Cleaning</a></li>
            <li><a href="#services" className="hover:text-white">Wash & Fold</a></li>
            <li><a href="#services" className="hover:text-white">Alterations</a></li>
            <li><a href="#services" className="hover:text-white">Comforters</a></li>
            <li><a href="#services" className="hover:text-white">Wedding Gowns</a></li>
            <li><a href="#faq" className="hover:text-white">{t("nav.faq")}</a></li>
          </ul>
        </div>

        <div className="md:col-span-4">
          <p className="font-display font-semibold uppercase text-sm tracking-[0.18em] text-slate-300">
            {t("footer.contactHeading")}
          </p>
          <ul className="mt-4 space-y-3 text-slate-300">
            <li className="flex items-start gap-3">
              <MapPin className="h-4 w-4 text-brand-blue mt-1 shrink-0" />
              <span>{BUSINESS.address}</span>
            </li>
            <li className="flex items-start gap-3">
              <Phone className="h-4 w-4 text-brand-blue mt-1 shrink-0" />
              <a href={`tel:${BUSINESS.phoneTel}`} className="hover:text-white">
                {BUSINESS.phone}
              </a>
            </li>
            <li className="flex items-start gap-3">
              <Mail className="h-4 w-4 text-brand-blue mt-1 shrink-0" />
              <a
                href={`mailto:${BUSINESS.email}`}
                className="hover:text-white break-all"
              >
                {BUSINESS.email}
              </a>
            </li>
          </ul>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-5 sm:px-8 mt-12 pt-6 border-t border-white/10 flex flex-col sm:flex-row items-center justify-between gap-4">
        <p className="text-sm text-slate-400">
          © {new Date().getFullYear()} Oxford Cleaners. {t("footer.rights")}
        </p>
        <p className="text-sm text-slate-400">
          {t("footer.serving")} {BUSINESS.serviceAreas.join(" · ")}
        </p>
      </div>
    </footer>
  );
}
